'use client';

import { useTrackPricingCardClick } from '@/hooks/useTrackPricingCardClick';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import Card from '@/components/atoms/Card';
import { Divider } from '@/components/atoms/Divider';
import LinkButtonStrapi from '@/components/atoms/LinkButton/LinkStrapi';
import { Text } from '@/components/atoms/Text';

import { createHrefWithExperimentTracking } from '@/blocks/PricingCards/utils';
import { usePathname } from 'next/navigation';
import PricingSectionBadgeOfHonor from '../Common/PricingSectionBadgeOfHonor';
import { PricingSectionMobileCardProps } from '../types';
import {
  getPricingCardTrackEventName,
  getPricingCardTrackExtra,
  getPricingCardTrackId,
} from '../utils';

const PricingSectionMobileCard = (props: PricingSectionMobileCardProps) => {
  const { texts } = useTranslation();
  const pathname = usePathname();
  const trackEventName = getPricingCardTrackEventName(props.type);
  const trackId = getPricingCardTrackId(props.ctaButton);
  const trackExtra = getPricingCardTrackExtra(
    props.headerTitle,
    props.ctaButton
  );

  const handleClick = useTrackPricingCardClick({
    trackEventName,
    trackId,
    trackExtra,
  });

  return (
    <Card
      classNames={{
        root: cn(
          'flex-1 flex flex-col gap-y-[1px]',
          'p-0 w-full flex-1',
          props.badgeOfHonor && '!border-brand-500'
        ),
      }}
    >
      {!!props.badgeOfHonor && (
        <PricingSectionBadgeOfHonor>
          {props.badgeOfHonor}
        </PricingSectionBadgeOfHonor>
      )}

      <div
        className={cn(
          'flex  p-4 justify-between w-full items-end pb-2',
          props.badgeOfHonor ? 'bg-brand-950' : 'bg-gray-dark-800',
          'md:min-h-[96px]'
        )}
      >
        <div className='flex flex-col'>
          {props.type === 'pricing_card' && (
            <Text variant='body-l' color='base-dark-white'>
              {texts.pricing_section.header_column_primary_text_mobile}
            </Text>
          )}
          <Text
            classNames={{ wrapper: 'text-h2 font-serif' }}
            color='base-dark-white'
          >
            {props.headerTitle}
          </Text>
        </div>
        {props.type === 'pricing_card' && (
          <Text
            variant='body-s'
            color='base-dark-white'
            classNames={{ wrapper: 'mb-2' }}
          >
            {texts.pricing_section.header_column_secondary_text}
          </Text>
        )}
      </div>
      <div
        className={cn(
          'w-full p-4',
          props.badgeOfHonor ? 'bg-brand-975' : 'bg-gray-dark-900'
        )}
      >
        <div className='flex justify-between mb-1'>
          <Text variant='body-s' color='gray-dark-400'>
            {texts.pricing_section.request_type}
          </Text>
          <Text variant='body-s' color='gray-dark-400'>
            {texts.pricing_section.price_per_1k_req}
          </Text>
        </div>

        <Divider showOnDark classNames={{ root: 'border-gray-dark-600' }} />

        {props.rows.map((row, index) => (
          <div key={index} className='flex justify-between mt-3'>
            {row.label && (
              <Badge
                classNames={{ root: 'h-5 light' }}
                size='s'
                color='gradient'
                variant='soft'
              >
                {row.label}
              </Badge>
            )}
            <div className='flex h-5'>
              {row.secondaryText && (
                <Text className='text-body-m text-gray-dark-400'>
                  {row.secondaryText}
                </Text>
              )}
              {row.primaryText && (
                <Text className='text-body-l text-base-dark-white ml-2'>
                  {row.primaryText}
                </Text>
              )}
            </div>
          </div>
        ))}

        <Divider
          showOnDark
          classNames={{ root: 'border-gray-dark-600 my-2' }}
        />

        {props.rateLimit && (
          <div className='flex flex-col w-full gap-y-2'>
            <div className='flex justify-between gap-y-2 mb-6'>
              <Text className='text-body-s text-gray-dark-400'>
                {texts.pricing_section.rate_limit}
              </Text>
              <Text className='text-body-m text-gray-dark-400'>
                {props.rateLimit}
              </Text>
            </div>
          </div>
        )}

        {props.ctaButton && (
          <LinkButtonStrapi
            block={{
              ...props.ctaButton,
              url: createHrefWithExperimentTracking(
                props.ctaButton.url,
                pathname
              ),
            }}
            inner={{
              isDecoro: true,
              'data-tracker-context': trackId,
              classNames: { root: 'w-full' },
              onClick: () => handleClick(),
            }}
          />
        )}
      </div>
    </Card>
  );
};

export default PricingSectionMobileCard;
